import type { LucideIcon } from "lucide-react";
import {
	AlertTriangleIcon,
	ClockAlertIcon,
	OctagonXIcon,
	RotateCcwIcon,
	ZapOffIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export type RunOutcome =
	| "cancelled_by_user"
	| "timed_out"
	| "failed"
	| "crashed";

interface RunOutcomeCardProps {
	outcome: RunOutcome;
	statusText?: string;
	onRetry?: () => void;
}

type Tone = "muted" | "warning" | "destructive";

interface OutcomeConfig {
	icon: LucideIcon;
	title: string;
	defaultStatusText?: string;
	suppressStatusText?: string;
	tone: Tone;
	retryable: boolean;
}

const OUTCOME_CONFIG: Record<RunOutcome, OutcomeConfig> = {
	cancelled_by_user: {
		icon: OctagonXIcon,
		title: "You stopped this run.",
		// runTurn's cancel path stamps this text — redundant with the title above.
		suppressStatusText: "Run cancelled by user",
		tone: "muted",
		retryable: false,
	},
	timed_out: {
		icon: ClockAlertIcon,
		title: "This run timed out and was stopped.",
		defaultStatusText:
			"Try again, or break the task into smaller steps. Large batches of attachments are a common cause.",
		tone: "warning",
		retryable: true,
	},
	failed: {
		icon: AlertTriangleIcon,
		title: "Something went wrong.",
		tone: "destructive",
		retryable: true,
	},
	crashed: {
		icon: ZapOffIcon,
		title: "Agent crashed mid-run.",
		defaultStatusText: "Agent stopped responding — please retry.",
		tone: "warning",
		retryable: true,
	},
};

// shadcn tokens (see app/src/index.css): muted=user-stopped, accent=recoverable, destructive=failure.
const TONE_CONTAINER: Record<Tone, string> = {
	muted: "border-border bg-muted/50",
	warning: "border-accent/40 bg-accent/10",
	destructive: "border-destructive/40 bg-destructive/10",
};

const TONE_ICON: Record<Tone, string> = {
	muted: "text-muted-foreground",
	warning: "text-accent",
	destructive: "text-destructive",
};

const TONE_TITLE: Record<Tone, string> = {
	muted: "text-muted-foreground",
	warning: "text-foreground",
	destructive: "text-destructive",
};

/**
 * Inline notice for a thread with a terminal `lastRunOutcome`. Retry sends
 * "continue" so the model resumes from partial progress, not a restart.
 */
export function RunOutcomeCard({
	outcome,
	statusText,
	onRetry,
}: RunOutcomeCardProps) {
	const config = OUTCOME_CONFIG[outcome];
	const Icon = config.icon;
	const description =
		statusText && statusText !== config.suppressStatusText
			? statusText
			: config.defaultStatusText;

	return (
		<div
			className="flex w-full max-w-[95%] flex-col gap-2"
			data-testid="chat-error"
			data-outcome={outcome}
		>
			<div
				className={cn(
					"flex items-start gap-3 rounded-lg border px-4 py-3 text-sm",
					TONE_CONTAINER[config.tone],
				)}
			>
				<Icon
					className={cn("mt-0.5 size-4 shrink-0", TONE_ICON[config.tone])}
				/>
				<div className="flex min-w-0 flex-1 flex-col gap-1">
					<div className={cn("font-medium", TONE_TITLE[config.tone])}>
						{config.title}
					</div>
					{description ? (
						<div className="text-xs text-muted-foreground">{description}</div>
					) : null}
				</div>
				{config.retryable && onRetry ? (
					<Button
						size="sm"
						variant="outline"
						onClick={onRetry}
						className="shrink-0 gap-1.5"
					>
						<RotateCcwIcon className="size-3.5" />
						Try again
					</Button>
				) : null}
			</div>
		</div>
	);
}
